import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import i18n from '@/app/i18n/config';
import { formatDate } from '@/lib/format';
import { useProfile, useUpdateProfile, useLogout, useAuth, useDevUpdateRole } from '@/hooks';
import type { UserRole } from '@/types';
import { Button, Input } from '@/components/ui';

type UpdateProfileFormData = {
  displayName?: string;
  password?: string;
};

export function ProfilePage(): JSX.Element {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  const logoutMutation = useLogout();
  const devUpdateRoleMutation = useDevUpdateRole();

  const createSchema = () =>
    z.object({
      displayName: z.string().optional(),
      password: z.string().min(8, t('validation.passwordMinLengthDefault')).optional().or(z.literal('')),
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(createSchema()),
    defaultValues: {
      displayName: profile?.displayName || '',
      password: '',
    },
  });

  useEffect(() => {
    if (profile) {
      reset({
        displayName: profile.displayName || '',
        password: '',
      });
    }
  }, [profile, reset]);

  const onSubmit = async (data: UpdateProfileFormData) => {
    try {
      const updateData: { displayName?: string; password?: string } = {};
      if (data.displayName !== undefined && data.displayName !== profile?.displayName) {
        updateData.displayName = data.displayName;
      }
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }

      if (Object.keys(updateData).length > 0) {
        await updateProfileMutation.mutateAsync(updateData);
      }
    } catch {
      // Error is handled by mutation
    }
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleDevRoleChange = (role: UserRole) => {
    devUpdateRoleMutation.mutate({ role });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('app.loading', { ns: 'common' })}</p>
      </div>
    );
  }

  if (!profile && !user) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-red-600">{t('genericError')}</p>
      </div>
    );
  }

  const displayUser = profile || user;
  const roleLabels: Record<string, string> = {
    CUSTOMER: t('profile.customer'),
    SELLER: t('profile.seller'),
    ADMINISTRATOR: t('profile.administrator'),
  };
  const devRoleOptions: UserRole[] = ['CUSTOMER', 'SELLER', 'ADMINISTRATOR'];
  const isBusy = isSubmitting || updateProfileMutation.isPending;

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-brand-700/20 bg-surface p-6">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="font-heading text-heading-xl text-brand-950">{t('profile.title')}</h1>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-800"
            onClick={handleLogout}
            disabled={logoutMutation.isPending}
            loading={logoutMutation.isPending}
          >
            {t('logout')}
          </Button>
        </div>

        <div className="mb-8 space-y-4 font-sans">
          <div>
            <label className="block text-sm font-medium text-brand-950">{t('profile.email')}</label>
            <p className="mt-1 text-sm text-brand-700">{displayUser?.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-950">{t('profile.role')}</label>
            <p className="mt-1 text-sm text-brand-700">
              {displayUser?.role ? roleLabels[displayUser.role] ?? displayUser.role : displayUser?.role}
            </p>
            {import.meta.env.DEV && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Dev role</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {devRoleOptions.map((role) => (
                    <Button
                      key={role}
                      type="button"
                      size="sm"
                      variant={displayUser?.role === role ? 'primary' : 'outline'}
                      onClick={() => handleDevRoleChange(role)}
                      disabled={devUpdateRoleMutation.isPending || displayUser?.role === role}
                    >
                      {roleLabels[role]}
                    </Button>
                  ))}
                </div>
                {devUpdateRoleMutation.isError && (
                  <p className="mt-2 text-sm text-red-700">{t('genericError')}</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-950">{t('profile.status')}</label>
            <p className="mt-1 text-sm text-brand-700">
              {displayUser?.isActive ? t('profile.active') : t('profile.inactive')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-950">{t('profile.createdAt')}</label>
            <p className="mt-1 text-sm text-brand-700">
              {displayUser?.createdAt ? formatDate(displayUser.createdAt, i18n.language) : '-'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <h2 className="font-heading text-heading-md text-brand-950">{t('profile.update')}</h2>

          <Input
            {...register('displayName')}
            id="displayName"
            type="text"
            autoComplete="name"
            label={t('profile.displayName')}
            error={errors.displayName?.message}
          />

          <Input
            {...register('password')}
            id="password"
            type="password"
            autoComplete="new-password"
            label={t('profile.newPassword')}
            error={errors.password?.message}
            hint={t('profile.changePassword')}
          />

          {updateProfileMutation.isError && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{t('genericError')}</p>
            </div>
          )}

          {updateProfileMutation.isSuccess && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">{t('common.success', { ns: 'common' })}</p>
            </div>
          )}

          <Button type="submit" fullWidth loading={isBusy} disabled={isBusy}>
            {isBusy ? t('app.loading', { ns: 'common' }) : t('common.save', { ns: 'common' })}
          </Button>
        </form>
      </div>
    </div>
  );
}
